export type ReportSeriesPoint = { date: string; value: number; label?: string; series?: string };

export type ChartDataPoint = {
  date: string;
  name: string;
  value: number;
  label?: string;
};

export const PIE_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

export const CHART_MARGINS = { top: 16, right: 24, left: 8, bottom: 48 } as const;

export const isTimeSeriesSeries = (series: ReportSeriesPoint[]): boolean =>
  series.length > 0 && /^\d{4}-\d{2}-\d{2}/.test(series[0].date);

const parseChartDate = (value: string): Date | null => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getSeriesTimeSpanMs = (data: ChartDataPoint[]): number => {
  if (data.length < 2) return 0;
  const first = parseChartDate(data[0].date)?.getTime() ?? 0;
  const last = parseChartDate(data[data.length - 1].date)?.getTime() ?? 0;
  return Math.abs(last - first);
};

export const toChartData = (series: ReportSeriesPoint[]): ChartDataPoint[] => {
  const points = series.map((point) => ({
    date: point.date,
    name: point.label ?? point.date,
    value: point.value,
    label: point.label,
  }));
  return isTimeSeriesSeries(series)
    ? [...points].sort(
        (a, b) => (parseChartDate(a.date)?.getTime() ?? 0) - (parseChartDate(b.date)?.getTime() ?? 0),
      )
    : [...points].sort((a, b) => b.value - a.value);
};

export const formatReportValue = (value: number, currency?: string, isPercentage?: boolean): string => {
  if (isPercentage) return `${(value * 100).toFixed(1)}%`;
  if (currency) {
    return `${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`;
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const formatChartAxisValue = (
  value: number,
  currency?: string,
  isPercentage?: boolean,
): string => {
  if (isPercentage) return `${Math.round(value * 100)}%`;
  const suffix = currency ? ` ${currency}` : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M${suffix}`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K${suffix}`;
  if (currency) return `${Math.round(value).toLocaleString()}${suffix}`;
  return Math.round(value).toLocaleString();
};

export const formatChartTooltipDate = (value: string): string => {
  const parsed = parseChartDate(value);
  if (!parsed) return value;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: dataHasHourComponent(value) ? '2-digit' : undefined,
    minute: dataHasHourComponent(value) ? '2-digit' : undefined,
  });
};

const dataHasHourComponent = (value: string): boolean =>
  /T\d{2}:\d{2}/.test(value) || /\d{2}:\d{2}:\d{2}/.test(value);

export const formatChartAxisDate = (value: string, spanMs: number): string => {
  const parsed = parseChartDate(value);
  if (!parsed) return String(value).slice(0, 10);
  if (spanMs > 0 && spanMs <= 3 * 86_400_000) {
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit' });
  }
  if (spanMs > 120 * 86_400_000) {
    return parsed.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const sumSeries = (series: ReportSeriesPoint[]): number =>
  series.reduce((total, point) => total + point.value, 0);

export const latestSeriesValue = (series: ReportSeriesPoint[]): number =>
  series.length ? series[series.length - 1].value : 0;

export const averageSeriesValue = (series: ReportSeriesPoint[]): number =>
  series.length ? sumSeries(series) / series.length : 0;
